import React, { useState, useEffect } from 'react';
import { 
  Users, 
  ClipboardCheck,
  ClipboardList,
  Library,
  School,
  Calendar,
  FileSpreadsheet,
  Presentation,
  Crown,
  BookMarked,
  UserCheck,
  CheckSquare,
  Clock,
  Loader2,
  GraduationCap,
  FileText,
  MessageSquare
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from '@/data/users';

// Main menu items with beautiful organization
const MENU_ITEMS = [
  {
    id: 'primary-gradebook',
    icon: GraduationCap,
    title: 'Grade Book',
    description: 'Enter and review marks for every class',
    route: '/dashboard/gradebook',
    color: 'from-emerald-500 to-green-600',
    borderColor: 'border-emerald-500',
    badge: 'Grades',
    priority: 1,
    primaryPrincipalOnly: true
  },
  {
    id: 'primary-report-cards',
    icon: FileText,
    title: 'Report Cards',
    description: 'View and publish report cards to parents',
    route: '/dashboard/view-report-cards',
    color: 'from-blue-500 to-indigo-600',
    borderColor: 'border-blue-500',
    badge: 'Reports',
    priority: 1,
    primaryPrincipalOnly: true
  },
  {
    id: 'primary-sms',
    icon: MessageSquare,
    title: 'SMS to Parents',
    description: 'Send messages and reminders to parents',
    route: '/dashboard/sms-notifications',
    color: 'from-purple-500 to-pink-600',
    borderColor: 'border-purple-500',
    badge: 'Parents',
    priority: 1,
    primaryPrincipalOnly: true
  },
  {
    id: 'principal',
    icon: Crown,
    title: 'Principal Dashboard',
    description: 'Comprehensive overview, analytics and quick access',
    route: '/dashboard/analytics',
    color: 'from-amber-500 to-orange-600',
    borderColor: 'border-amber-500',
    badge: 'Executive',
    priority: 1,
    adminOnly: true // Restrict to admin/principal only
  },
  {
    id: 'grade-completion',
    icon: CheckSquare,
    title: 'Grade Completion',
    description: 'Check grade status before publishing reports',
    route: '/dashboard/grade-completion',
    color: 'from-emerald-500 to-green-600',
    borderColor: 'border-emerald-500',
    badge: 'Admin',
    priority: 1,
    adminOnly: true // Restrict to admin/principal only
  },
  {
    id: 'publish-reports',
    icon: FileSpreadsheet,
    title: 'Publish Reports',
    description: 'Publish report cards to parent portal',
    route: '/dashboard/publish-reports',
    color: 'from-purple-500 to-pink-600',
    borderColor: 'border-purple-500',
    badge: 'Principal',
    priority: 1,
    adminOnly: true // Restrict to admin/principal only
  },
  {
    id: 'attendance',
    icon: ClipboardCheck,
    title: 'Roll Call',
    description: 'Take attendance for each daily period',
    route: '/dashboard/roll-call',
    color: 'from-blue-500 to-cyan-600',
    borderColor: 'border-blue-500',
    badge: 'Attendance',
    priority: 1
  },
  {
    id: 'students',
    icon: Users,
    title: 'Students',
    description: 'View and manage students in your classes',
    route: '/dashboard/students',
    color: 'from-green-500 to-emerald-600',
    borderColor: 'border-green-500',
    badge: 'Directory',
    priority: 1
  },
  {
    id: 'ia-program',
    icon: ClipboardCheck,
    title: 'IA Program',
    description: 'Internal Assessment tasks, drafts, due dates & student submissions',
    route: '/dashboard/ia-program',
    color: 'from-emerald-600 to-green-700',
    borderColor: 'border-emerald-600',
    badge: 'IA',
    priority: 1,
    secondaryOnly: true
  },
  {
    id: 'gradebook',
    icon: FileSpreadsheet,
    title: 'Grade Book',
    description: 'Student grades and academic performance',
    route: '/dashboard/gradebook',
    color: 'from-purple-500 to-violet-600',
    borderColor: 'border-purple-500',
    badge: 'Grades',
    priority: 2
  },
  {
    id: 'student-work',
    icon: Presentation,
    title: 'Student Work',
    description: 'Review and give feedback on student submissions',
    route: '/dashboard/student-submissions',
    color: 'from-orange-500 to-amber-600',
    borderColor: 'border-orange-500',
    badge: 'Review',
    priority: 2
  },
  {
    id: 'activities',
    icon: ClipboardList,
    title: 'Activities & Tests',
    description: 'Chapter activities and auto-marked tests from the booklets',
    route: '/dashboard/activities',
    color: 'from-pink-500 to-rose-600',
    borderColor: 'border-pink-500',
    badge: 'Tests',
    priority: 2,
    secondaryOnly: true
  },
  {
    id: 'primary-curriculum',
    icon: BookMarked,
    title: 'Primary Curriculum',
    description: 'Year 1-3 curriculum, planners & resources',
    route: '/dashboard/primary-curriculum',
    color: 'from-cyan-500 to-teal-600',
    borderColor: 'border-cyan-500',
    badge: 'Y1-3',
    priority: 2,
    primaryOnly: true
  },
  {
    id: 'resource-library',
    icon: Library,
    title: 'Resource Library',
    description: 'Textbooks, activity booklets, answer keys & planning files',
    route: '/dashboard/resource-library',
    color: 'from-teal-500 to-cyan-600',
    borderColor: 'border-teal-500',
    badge: 'Library',
    priority: 3,
    secondaryOnly: true
  },
  {
    id: 'classes',
    icon: School,
    title: 'Classes',
    description: 'Homeroom and subject class management',
    route: '/dashboard/classes',
    color: 'from-lime-500 to-green-600',
    borderColor: 'border-lime-500',
    badge: 'Classes',
    priority: 3
  },
  {
    id: 'timetable',
    icon: Clock,
    title: 'Timetable',
    description: 'View your weekly teaching timetable',
    route: '/dashboard/timetable',
    color: 'from-purple-500 to-indigo-600',
    borderColor: 'border-purple-500',
    badge: 'Weekly',
    priority: 2
  }
];

// Timetable constants
const TIMETABLE_PERIODS = [
  { number: 1, start: "07:30", end: "08:30" },
  { number: 2, start: "08:30", end: "09:30" },
  { number: 3, start: "10:00", end: "11:00" },
  { number: 4, start: "11:00", end: "12:00" },
  { number: 5, start: "13:00", end: "13:50" },
  { number: 6, start: "13:50", end: "14:40" },
  { number: 7, start: "14:40", end: "15:30" },
];
const TIMETABLE_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

export default function MenuPage() {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';
  const isPrincipal = currentUser?.role === 'principal';
  const isTeacher = currentUser?.role === 'teacher';
  const [schoolName, setSchoolName] = useState('Penama Education Portal');
  
  // Teacher's personal timetable state
  const [mySchedule, setMySchedule] = useState(null);
  const [timetableLoading, setTimetableLoading] = useState(false);
  
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser?.school_id) return;
      
      const API_URL = '';
      const token = localStorage.getItem('authToken');
      
      // Fetch school name
      try {
        const response = await fetch(`${API_URL}/api/schools/${currentUser.school_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setSchoolName(data.name || 'Penama Education Portal');
        }
      } catch (error) {
        console.error('Error fetching school:', error);
      }
      
      // Fetch teacher's personal timetable
      if (isTeacher) {
        setTimetableLoading(true);
        try {
          const timetableRes = await fetch(`${API_URL}/api/timetable/my-timetable`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (timetableRes.ok) {
            const timetableData = await timetableRes.json();
            setMySchedule(timetableData.schedule);
          }
        } catch (err) {
          console.error('Error fetching personal timetable:', err);
        }
        setTimetableLoading(false);
      }
    };
    
    fetchData();
  }, [currentUser?.school_id, isTeacher]);

  // Render teacher's schedule cell - supports multiple classes per period
  const renderScheduleCell = (day, periodNumber) => {
    if (!mySchedule || !mySchedule[day]) return <div className="text-xs text-gray-400 text-center">Free</div>;
    
    const periods = mySchedule[day];
    // Find ALL matching periods (teacher may have multiple Option classes at same time)
    const matchingPeriods = periods.filter(p => {
      if (p.is_double) {
        return periodNumber >= p.period_start && periodNumber <= p.period_end;
      }
      return p.period === periodNumber;
    });
    
    if (matchingPeriods.length === 0) {
      return <div className="text-xs text-gray-400 text-center">Free</div>;
    }
    
    // Check for double period - skip second cell
    const doublePeriod = matchingPeriods.find(p => p.is_double);
    if (doublePeriod && periodNumber > doublePeriod.period_start) {
      return null;
    }
    
    // Render all classes stacked
    return (
      <div className="space-y-1">
        {matchingPeriods.map((periodData, idx) => {
          const isDouble = periodData.is_double && periodNumber === periodData.period_start;
          return (
            <div key={idx} className="p-1 bg-purple-50 border-l-2 border-purple-500 rounded text-xs">
              <div className="font-semibold text-purple-900 truncate">{periodData.subject}</div>
              <div className="text-purple-600 truncate">{periodData.class_name}</div>
              {isDouble && <div className="text-purple-500 font-medium">Double</div>}
            </div>
          );
        })}
      </div>
    );
  };

  // Filter menu items based on user role and school type
  const isPrimaryPrincipal = isPrincipal && currentUser?.school_type === 'primary';
  const filteredMenuItems = MENU_ITEMS.filter(item => {
    if (isPrimaryPrincipal) return item.primaryPrincipalOnly;
    if (item.primaryPrincipalOnly) return false;
    // If user is principal, show Principal Dashboard + admin-only items
    if (isPrincipal) {
      return item.id === 'principal' || item.adminOnly;
    }
    
    // If item requires admin access, check if user is admin
    if (item.adminOnly) {
      return isAdmin;
    }
    
    // Check school type for curriculum items
    const isPrimarySchool = currentUser?.school_type === 'primary';
    
    if (item.primaryOnly && !isPrimarySchool) return false;
    if (item.secondaryOnly && isPrimarySchool) return false;
    
    return true;
  });

  // Group items by priority
  const priorityGroups = {
    1: filteredMenuItems.filter(item => item.priority === 1),
    2: filteredMenuItems.filter(item => item.priority === 2),
    3: filteredMenuItems.filter(item => item.priority === 3)
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 -m-6 p-6">
      <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500">
        {/* Header with Manuscript Style */}
        <div className="text-center space-y-2">
          {/* Penama Text Above Logo */}
          <h1 className="text-6xl font-bold tracking-wider text-primary mb-2" style={{ fontFamily: "'Arial', sans-serif", letterSpacing: '0.15em' }}>
            PENAMA
          </h1>
          
          {/* Logo in Center with Enhanced Circular Frame */}
          <div className="flex items-center justify-center mb-4">
            <div className="relative">
              {/* Animated gradient ring */}
              <div className="absolute -inset-2 bg-gradient-to-r from-blue-600 via-primary to-blue-600 rounded-full opacity-20 blur-sm animate-pulse"></div>
              <div className="relative w-52 h-52 rounded-full bg-white border-4 border-primary shadow-2xl flex items-center justify-center p-6">
                <img 
                  src="/penama-logo.png" 
                  alt="Penama Education Portal" 
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
          
          {/* Education Portal Text Below Logo */}
          <h2 className="text-5xl font-bold tracking-wide text-primary mb-3" style={{ fontFamily: "'Great Vibes', cursive" }}>
            Education Portal
          </h2>
          
          <div className="space-y-2">
            <p className="text-lg text-muted-foreground italic">
              Empowering Education Across Penama Province
            </p>
            <div className="flex items-center justify-center gap-2">
              <div className="h-1 w-8 bg-primary rounded"></div>
              <p className="text-sm font-semibold text-primary">
                {schoolName}
              </p>
              <div className="h-1 w-8 bg-primary rounded"></div>
            </div>
          </div>
          <div className="flex justify-center gap-3 flex-wrap">
            <Badge variant="outline" className="text-sm py-1 px-4">
              <UserCheck className="h-3 w-3 mr-1" />
              Teacher Access
            </Badge>
            <Badge variant="outline" className="text-sm py-1 px-4">
              <Calendar className="h-3 w-3 mr-1" />
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </Badge>
          </div>
        </div>

        {/* Teacher's Weekly Timetable - Only for teachers */}
        {isTeacher && (
          <Card className="border-2 border-purple-200 shadow-lg bg-gradient-to-br from-purple-50 to-indigo-50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl flex items-center gap-2 text-purple-900">
                    <Calendar className="h-6 w-6" />
                    My Weekly Timetable
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Your personal teaching schedule for this week
                  </CardDescription>
                </div>
                <Badge className="bg-purple-600">
                  {currentUser?.name || 'Teacher'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {timetableLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                </div>
              ) : mySchedule ? (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-purple-100">
                        <th className="border border-purple-200 p-2 text-left font-semibold text-purple-900">
                          <Clock className="w-4 h-4 inline mr-1" />
                          Period
                        </th>
                        {TIMETABLE_DAYS.map(day => (
                          <th key={day} className="border border-purple-200 p-2 text-center font-semibold text-purple-900">
                            {day}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {TIMETABLE_PERIODS.map((period) => (
                        <React.Fragment key={period.number}>
                          {/* Break after period 2 */}
                          {period.number === 3 && (
                            <tr className="bg-amber-50">
                              <td colSpan={6} className="border border-amber-200 p-1 text-center text-xs font-medium text-amber-700">
                                ☕ Break: 9:30 - 10:00
                              </td>
                            </tr>
                          )}
                          {/* Lunch after period 4 */}
                          {period.number === 5 && (
                            <tr className="bg-orange-50">
                              <td colSpan={6} className="border border-orange-200 p-1 text-center text-xs font-medium text-orange-700">
                                🍽️ Lunch: 12:00 - 1:00
                              </td>
                            </tr>
                          )}
                          <tr className="hover:bg-purple-50/50">
                            <td className="border border-gray-200 p-2 bg-gray-50">
                              <div className="font-medium text-gray-900">P{period.number}</div>
                              <div className="text-xs text-gray-500">{period.start}-{period.end}</div>
                            </td>
                            {TIMETABLE_DAYS.map(day => (
                              <td key={`${day}-${period.number}`} className="border border-gray-200 p-1 min-w-[100px]">
                                {renderScheduleCell(day, period.number)}
                              </td>
                            ))}
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                  <p>No timetable assigned yet</p>
                </div>
              )}
              
              {/* Legend */}
              {mySchedule && (
                <div className="mt-4 flex items-center gap-6 text-xs text-gray-600">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-purple-500 rounded"></div>
                    <span>Teaching Period</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gray-300 rounded"></div>
                    <span>Free Period</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Essential Modules */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {priorityGroups[1].map((item) => (
              <Card 
                key={item.id}
                className={`cursor-pointer hover:shadow-2xl transition-all duration-300 hover:scale-105 border-l-4 ${item.borderColor} group relative overflow-hidden`}
                onClick={() => navigate(item.route)}
              >
                {/* Gradient overlay on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
                
                <CardHeader className="relative">
                  <div className="flex items-start justify-between">
                    <div className={`p-4 rounded-xl bg-gradient-to-br ${item.color} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <item.icon className="h-8 w-8 text-white" />
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {item.badge}
                    </Badge>
                  </div>
                  <CardTitle className="text-2xl mt-4" style={{ fontFamily: "'Dancing Script', cursive", fontWeight: 700 }}>
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="relative">
                  <p className="text-sm text-muted-foreground">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Academic Management */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {priorityGroups[2].map((item) => (
              <Card 
                key={item.id}
                className={`cursor-pointer hover:shadow-2xl transition-all duration-300 hover:scale-105 border-l-4 ${item.borderColor} group relative overflow-hidden`}
                onClick={() => navigate(item.route)}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
                
                <CardHeader className="relative">
                  <div className="flex items-start justify-between">
                    <div className={`p-4 rounded-xl bg-gradient-to-br ${item.color} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <item.icon className="h-8 w-8 text-white" />
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {item.badge}
                    </Badge>
                  </div>
                  <CardTitle className="text-2xl mt-4" style={{ fontFamily: "'Dancing Script', cursive", fontWeight: 700 }}>
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="relative">
                  <p className="text-sm text-muted-foreground">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Additional Tools */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {priorityGroups[3].map((item) => (
              <Card 
                key={item.id}
                className={`cursor-pointer hover:shadow-2xl transition-all duration-300 hover:scale-105 border-l-4 ${item.borderColor} group relative overflow-hidden`}
                onClick={() => navigate(item.route)}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
                
                <CardHeader className="relative">
                  <div className="flex items-start justify-between">
                    <div className={`p-4 rounded-xl bg-gradient-to-br ${item.color} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <item.icon className="h-8 w-8 text-white" />
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {item.badge}
                    </Badge>
                  </div>
                  <CardTitle className="text-2xl mt-4" style={{ fontFamily: "'Dancing Script', cursive", fontWeight: 700 }}>
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="relative">
                  <p className="text-sm text-muted-foreground">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center py-8 border-t">
          <p className="text-sm text-muted-foreground">
            © 2024 Penama Education Portal • Penama Province, Vanuatu
          </p>
        </div>
      </div>
    </div>
  );
}
